import React from 'react';
import { Flex, Popover, Tabs } from 'antd';
import { FaInfoCircle } from 'react-icons/fa';
import {
	StyledCodeExample,
	StyledHighlightCode,
	StyledPopoverContainer,
	StyledTabContainer,
	StyledTitle,
	StyledTutorialIcon
} from 'src/pages/observers/components/CodeEditor/VariablesTutorial/styles';
import { GoDotFill } from 'react-icons/go';

export default function VariablesTutorial() {
	return (
		<Popover
			trigger={[ 'click', ]}
			content={
				<StyledPopoverContainer>
					<Flex vertical>
						<StyledTitle>
							Usando Variáveis em Consultas SQL
						</StyledTitle>
						<p style={{ textAlign: 'justify', }}>
							Ao inserir variáveis no SQL, os campos de
							input correspondentes serão gerados
							automaticamente abaixo da consulta. Esses
							campos permitem que o usuário preencha os
							valores necessários para filtrar os dados de
							acordo com as condições definidas.
						</p>
					</Flex>
					<Tabs
						size='small'
						tabPosition='top'
						centered
						items={[
							{
								key: '1',
								label: <GoDotFill />,
								children: <StyledTabContainer>
									<StyledTitle>
										Variáveis simples
									</StyledTitle>
									<Flex vertical gap={8}>
										<p>
											Você pode usar variáveis em suas consultas SQL para que o
											usuário preencha o valor de um filtro.
										</p>
										<Flex vertical>
											<strong>• Sintaxe:</strong>
											<p><i>{'{{nome_da_variável/tipo_da_variavel}}'}</i></p>
										</Flex>
										<Flex vertical>
											<strong>• Explicação:</strong>
											<div>
												<p>
													<i>{'{{nome_da_variável/tipo_da_variavel}} '}</i>
													cria um campo de input onde o usuário pode digitar
													o nome do cliente.
												</p>
												<p>
													O tipo da variável (text, number, date, boolean)
													determina o tipo de dado que o usuário deve fornecer.
													Caso o tipo não seja fornecido, o tipo padrão é text.
												</p>
											</div>
										</Flex>
										<Flex vertical>
											<strong>Exemplo:</strong>
											<StyledCodeExample>
												<StyledHighlightCode>SELECT</StyledHighlightCode>
												{' * '}
												<StyledHighlightCode>FROM</StyledHighlightCode>
												{' clients '}
												<StyledHighlightCode>WHERE</StyledHighlightCode>
												{' client_name = {{nome do cliente/text}}'}
											</StyledCodeExample>
										</Flex>
									</Flex>
								</StyledTabContainer>,

							},
							{
								key: '2',
								label: <GoDotFill />,
								children: <StyledTabContainer>
									<StyledTitle>
										Variáveis para condições complexas
									</StyledTitle>
									<Flex vertical gap={8}>
										<p>
											Você também pode criar variáveis que permitem adicionar
											condições mais complexas usando uma interface visual.
										</p>
										<Flex vertical>
											<strong>• Sintaxe:</strong>
											<p>
												<i>
													{'{{nome_da_variavel columns(\'nome_da_coluna/tipo_da_coluna\')}}'}
												</i>
											</p>
										</Flex>
										<Flex vertical>
											<strong>• Explicação:</strong>
											<p>
												<i>
													{'{{nome_da_variavel columns(\'nome_da_coluna/tipo_da_coluna\')}} '}
												</i>
												permite ao usuário criar uma condição complexa e não
												apenas um valor único, como {' "nome_da_coluna >= 30"'}
											</p>
										</Flex>
										<Flex vertical>
											<strong>Exemplo:</strong>
											<StyledCodeExample>
												<StyledHighlightCode>SELECT</StyledHighlightCode>
												{' * '}
												<StyledHighlightCode>FROM</StyledHighlightCode>
												{' clients '}
												<StyledHighlightCode>WHERE</StyledHighlightCode>
												{' {{condicoes columns(\'idade/number\', \'name/text\')}}'}
											</StyledCodeExample>
										</Flex>
									</Flex>
								</StyledTabContainer>,
							},
							{
								key: '3',
								label: <GoDotFill />,
								children: <StyledTabContainer>
									<StyledTitle>
										Condições opcionais
									</StyledTitle>
									<Flex vertical gap={8}>
										<p>
											Você pode tornar partes da sua consulta SQL opcionais. Para
											isso, basta colocar a condição entre colchetes([ ]). Se a
											variável dentro dos colchetes for preenchida, a condição será
											incluída na consulta final; se não for preenchida, a condição
											será ignorada.
										</p>
										<Flex vertical>
											<strong>• Sintaxe:</strong>
											<p>
												<i>
													[conteúdo opcional com condição]
												</i>
											</p>
										</Flex>
										<Flex vertical>
											<strong>• Explicação:</strong>
											<p>
												Se as variáveis dentro dos colchetes forem preenchidas, o
												conteúdo será incluída na consulta final. Caso contrário,
												todo o conteúdo dentro dos colchetes([ ]) será ignorada.
												Isso permite criar consultas mais flexíveis, onde certas
												condições só são aplicadas se o usuário fornecer os valores
												correspondentes.
											</p>
										</Flex>
										<Flex vertical>
											<strong>Exemplo:</strong>
											<StyledCodeExample>
												<StyledHighlightCode>SELECT</StyledHighlightCode>
												{' * '}
												<StyledHighlightCode>FROM</StyledHighlightCode>
												{' clients [ '}
												<StyledHighlightCode>WHERE</StyledHighlightCode>
												{' client_name = {{nome do cliente/string}} ]'}
											</StyledCodeExample>
										</Flex>
										<Flex vertical>
											<strong>• Observação:</strong>
											<p>
												Se houver mais de uma variável dentro dos colchetes([ ]),
												todas devem ser preenchidas para que a condição seja
												incluída na consulta final.
											</p>
										</Flex>
									</Flex>
								</StyledTabContainer>,
							},
							{
								key: '4',
								label: <GoDotFill />,
								children: <StyledTabContainer>
									<StyledTitle>
										Tipos de valores
									</StyledTitle>
									<Flex vertical gap={8}>
										<p>
											<strong>• Data:</strong> {' {{var/date}}'}
										</p>
										<p>
											<strong>• Texto:</strong> {' {{var/text}}'}:
										</p>
										<p>
											<strong>• Número:</strong> {' {{var/number}}'}
										</p>
										<p>
											<strong>• Boleano(verdadeiro/falso):</strong> {' {{var/boolean}}'}
										</p>
									</Flex>
								</StyledTabContainer>,
							},
						]}
					/>
				</StyledPopoverContainer >
			}
		>
			<StyledTutorialIcon icon={<FaInfoCircle />} type='text' />
		</Popover >
	);
}
